import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { DATASET, INTERNAL_UNKNOWN } from '@utils/constants';
import { get } from 'lodash';
import { getFirstAppliedFilter } from '@store/common/selectors';
import { getDimensionData } from '@src/api/dimensionContainer';
import { viewerSessionMetricList } from './utils';
import { metricKeys } from '@pulse/metrics';
import moment from 'moment';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';

const defaultSort = { key: metricKeys.BadSession, order: 'desc' };

export const query = {
  type: 'group-by',
  dataset: DATASET,
  metrics: viewerSessionMetricList,
  interval: '2018-12-19T22:35:18.907Z/2018-12-20T22:35:18.907Z',
  granularity: 'ALL',
  filter: [],
  limit: 100,
  groupBy: ['VIEWER_ID'],
  orderBy: 'desc',
  sortBy: [{ metric: metricKeys.BadSession, order: 'desc' }],
  options: {
    withTotals: false,
  },
};

function buildQuery({ filter, interval, sortBy, metricFilter, kpiConfig }) {
  const { key, order } = sortBy;
  const q: any = cloneDeep(query);
  q.filter = filter;
  q.interval = `${moment(interval.from).format()}/${moment(interval.to).format()}`;
  q.sortBy = [{ metric: key, order }];
  q.options.kpiConfig = kpiConfig ?? {};

  if (metricFilter && metricFilter.length > 0) {
    q.metricFilter = metricFilter;
  }

  return q;
}

function useFetchData({ enabled = true }) {
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const kpiConfig = useCurrentKpiConfig();

  const [sortBy, setSortBy] = useState(defaultSort);

  const filter = appliedFilter.rule;
  const query = buildQuery({ metricFilter, filter, interval, sortBy, kpiConfig });

  const onColumnSort = ({ key }) => {
    setSortBy({ key, order: 'desc' });
  };

  const { status, data, refetch } = useQuery(
    ['trends-viewer-sessions', query, kpiConfig],
    () =>
      getDimensionData({
        queries: [query],
      }),
    {
      // default cacheTime is 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
      select: (response) => {
        const rows = get(response, ['data', 0, 'data', 0, 'results'], []);
        const unknownRows: any = [];
        // move unknown rows to the bottom
        const filteredRows = rows.filter((row) => {
          if (row.VIEWER_ID === INTERNAL_UNKNOWN) {
            unknownRows.push(row);
            return false;
          }
          return true;
        });

        return filteredRows.concat(unknownRows);
      },
    },
  );

  return {
    status,
    rows: data,
    refetch,
    sortBy,
    onColumnSort,
  };
}

export default useFetchData;
